<template>
  <div :class="['how-it-works', isIs24 ? 'is24' : 'hg']">
    <div class="images">
      <img
        v-if="!isIs24"
        class="gradient-heart"
        :src="gradientHeartUrl"
        alt=""
        aria-hidden="true"
        loading="lazy"
      />
      <div class="background-image"></div>
      <div class="price-chart">
        <img :src="chartImageUrl" :alt="$t('landing.img.pricechart.alt')" />
      </div>
    </div>
    <div class="steps">
      <h2>{{ $t('landing.howitworks.title') }}</h2>
      <div v-for="(item, index) in howitworksSteps" :key="item.title" class="step">
        <div class="left">
          <span class="number">{{ index + 1 }}</span>
        </div>
        <div class="right">
          <h3>{{ $t(item.title) }}</h3>
          <p>{{ $t(item.text) }}</p>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue';

import gradientHeartUrl from '@/assets/images/effects/gradient-heart.webp';
import landingPriceChartHg from '@/assets/images/landing-valuation/landing-price-chart-hg.svg?url';
import landingPriceChartIs24 from '@/assets/images/landing-valuation/landing-price-chart-is24.svg?url';
import { getCurrentPlatform, isPlatformIs24 } from '@/helpers/Platform/Platform';

const isIs24 = isPlatformIs24();
const platform = getCurrentPlatform();
const chartImageUrl = computed(() =>
  platform === 'is24' ? landingPriceChartIs24 : landingPriceChartHg,
);

const howitworksSteps = ref([
  {
    title: 'landing.howitworks.input.title',
    text: 'landing.howitworks.input.text',
  },
  {
    title: 'landing.howitworks.calculation.title',
    text: 'landing.howitworks.calculation.text',
  },
  {
    title: 'landing.howitworks.results.title',
    text: 'landing.howitworks.results.text',
  },
  {
    title: 'landing.howitworks.support.title',
    text: 'landing.howitworks.support.text',
  },
]);
</script>

<style lang="scss" scoped>
@use '@/assets/variables' as *;

h2 {
  margin: 0;

  @include heading-2($color-grey-120);
}

h3 {
  @include heading-3($color-grey-120);
}

.how-it-works {
  .images {
    position: relative;
    margin-bottom: $space-400;
    overflow-x: clip;

    .gradient-heart {
      position: absolute;
      top: -$space-500;
      left: 45%;
      z-index: 0;
      width: calc($space-1000 * 2 + $space-500);
      height: calc($space-1000 * 2 + $space-500);
      pointer-events: none;
      object-fit: contain;
      transform: translateX(-33%) rotate(-90deg);
    }

    .background-image {
      position: relative;
      z-index: 1;
      width: 100%;
      height: 208px;
      background: url('@/assets/images/landing-howitworks/landing-howitworks-image-hg.jpg')
        no-repeat center top;
      background-size: 100% auto;
      border-radius: $border-radius-l;

      @include grid-media-query(sm) {
        height: 336px;
      }
    }

    .price-chart {
      position: absolute;
      z-index: 2;
      right: 0;
      left: 0;
      width: 248px;
      height: 182px;
      margin: 0 auto;
      border-radius: $border-radius-l;
      box-shadow: $elevation-medium;

      img {
        width: 100%;
        height: 100%;
      }

      @include grid-media-query(md) {
        width: 330px;
        height: 240px;
      }
    }
  }

  .steps {
    display: flex;
    flex-direction: column;
    gap: $space-250;

    h2 {
      margin-bottom: $space-50;
    }

    .step {
      display: flex;
      flex-direction: row;

      .left {
        padding-top: $space-25;
        padding-right: $space-100;

        .number {
          @include heading-4(var(--message-color-info-icon));

          & {
            display: inline-block;
            width: $space-200;
            height: $space-200;
            line-height: $space-200;
            text-align: center;
            background-color: var(--message-color-info-background);
            border-radius: 50%;

            @include grid-media-query(md) {
              width: $space-250;
              height: $space-250;
              line-height: $space-250;
            }
          }
        }
      }

      .right {
        h3 {
          margin: 0;
          line-height: $space-200;
        }

        p {
          margin: 0;
          color: $color-grey-120;

          @include body-2;
        }
      }
    }
  }

  &.hg {
    display: flex;
    flex-direction: column;

    @include grid-media-query(sm) {
      flex-direction: row;
      margin-top: $space-600;
    }

    .images {
      @include grid-media-query(sm) {
        position: sticky;
        top: $space-100;
        align-self: flex-start;
        margin-top: $space-150;
        margin-bottom: 0;
      }

      .background-image {
        @include grid-media-query(sm) {
          width: 198px;
          margin-right: 138px;
          background-position: center;
          background-size: auto 100%;
        }

        @include grid-media-query(md) {
          width: 283px;
          height: 480px;
          margin-right: 197px;
        }
      }

      .gradient-heart {
        @include grid-media-query(sm) {
          top: -$space-150;
          left: 0;
          transform: rotate(-90deg);
        }

        @include grid-media-query(md) {
          top: 0;
          left: $space-800;
        }
      }

      .price-chart {
        top: 50px;

        @include grid-media-query(sm) {
          top: $space-400;
          right: unset;
          left: $space-400;
          margin: 0;
        }

        @include grid-media-query(md) {
          top: $space-500;
          left: $space-800;
        }
      }
    }

    .steps {
      @include grid-media-query(sm) {
        margin-left: $space-400;
      }
    }
  }

  &.is24 {
    max-width: 100%;
    margin-right: auto;
    margin-left: auto;

    @include grid-media-query(md) {
      max-width: 800px;
    }

    .images {
      .background-image {
        background-image: url('@/assets/images/landing-howitworks/landing-howitworks-image-is24.jpg');
        background-size: cover;
      }

      .price-chart {
        bottom: -$space-400;

        @include grid-media-query(sm) {
          width: 330px;
          height: 240px;
        }

        @include grid-media-query(md) {
          bottom: -4.5rem;
        }
      }
    }

    .steps {
      padding-top: $space-400;
      text-align: justify;

      @include grid-media-query(md) {
        padding-top: 4.5rem;
        text-align: left;
      }

      .left {
        @include grid-media-query(sm) {
          padding-right: $space-150;
        }

        .number {
          color: $color-cyan-130;
          background-color: $color-cyan-60;

          @include grid-media-query(sm) {
            width: $space-250;
            height: $space-250;
            line-height: $space-250;
          }
        }
      }
    }
  }
}
</style>
